// src/pages/MonthlySummaryPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCalendarAlt, FaChevronLeft, FaChevronRight, FaSyncAlt } from "react-icons/fa";
import { printThermal, printThermalViaUsb} from "../utils/exportHelpers";
import { formatStoredMoney } from "../utils/currency";
import { API_BASE_URL } from "../config/constants";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import { getOfflineMonthlySummary } from "../offline/financeStore";

const COLOR = {
  primary: "var(--app-primary)",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
};

const MonthlySummaryPage = () => {
  const [userId, setUserId] = useState(null);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [data, setData] = useState([]); // {month, total_earnings}
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    if (!userId) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, year]);

  const fetchData = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    try {
      if (isFullOfflineModeEnabled()) {
        const summary = await getOfflineMonthlySummary(userId, year);
        setData(Array.isArray(summary) ? summary : []);
        return;
      }
      const res = await fetch(`${API_BASE_URL}/monthsummary`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: userId, year }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Failed to load summary");
      setData(Array.isArray(json) ? json : []);
    } catch (e) {
      toast.error(e.message || "Unable to load summary");
      setData([]);
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const total = data.reduce((s, d) => s + Number(d.total_earnings || 0), 0);
    const best = data.reduce(
      (best, d) =>
        Number(d.total_earnings || 0) > best.value
          ? { label: d.month, value: Number(d.total_earnings || 0) }
          : best,
      { label: "-", value: 0 }
    );
    const maxValue = data.reduce((m, d) => Math.max(m, Number(d.total_earnings || 0)), 0);
    return { total, best, maxValue: maxValue || 1 };
  }, [data]);

  const fmtINR = (n) => formatStoredMoney(n, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const changeYear = (delta) => {
    const next = year + delta;
    const thisYear = new Date().getFullYear();
    if (next > thisYear) return;
    setYear(next);
  };

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <Top>
        <div>
          <Title>Monthly Earnings Summary</Title>
          <Sub>View totals by month for {year}</Sub>
          <PillRow>
            <Pill>Total: {fmtINR(stats.total)}</Pill>
            <Pill $muted>Top: {stats.best.label} {fmtINR(stats.best.value)}</Pill>
          </PillRow>
        </div>
        <ActionRow>
          <YearControl>
            <NavBtn onClick={() => changeYear(-1)}>
              <FaChevronLeft />
            </NavBtn>
            <YearBox>
              <FaCalendarAlt />
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(Number(e.target.value) || year)}
                min="2000"
                max={new Date().getFullYear()}
              />
            </YearBox>
            <NavBtn
              onClick={() => changeYear(1)}
              disabled={year >= new Date().getFullYear()}
              style={{ opacity: year >= new Date().getFullYear() ? 0.5 : 1 }}
            >
              <FaChevronRight />
            </NavBtn>
          </YearControl>
          <PrimaryBtn onClick={() => fetchData({ refresh: true })}>
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
          </PrimaryBtn>
          <PrimaryBtn onClick={() => printThermal(reportRef.current, `monthly_summary_${year}`)}>
            Thermal Print
          </PrimaryBtn><PrimaryBtn onClick={() => printThermalViaUsb(reportRef.current, `monthly_summary_${year}`)}>
            Thermal Print via USB
          </PrimaryBtn>
        </ActionRow>
      </Top>

      <Card ref={reportRef}>
        {loading ? (
          <Center>Loading…</Center>
        ) : data.length === 0 ? (
          <Center>No data found for this year.</Center>
        ) : (
          <Bars>
            {data.map((d, idx) => {
              const value = Number(d.total_earnings || 0);
              const width = Math.max(6, (value / stats.maxValue) * 100);
              return (
                <BarRow key={idx}>
                  <span className="label">{d.month}</span>
                  <BarTrack>
                    <BarFill style={{ width: `${width}%` }} />
                  </BarTrack>
                  <span className="val">{fmtINR(value)}</span>
                </BarRow>
              );
            })}
          </Bars>
        )}
      </Card>
    </Page>
  );
};

export default MonthlySummaryPage;

/* ---------------- styled ---------------- */
const Page = styled.div`
  background: ${COLOR.bg};
  min-height: 100vh;
  padding: 16px;
`;
const Top = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;
const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color: ${COLOR.text};
`;
const Sub = styled.div`
  color: ${COLOR.muted};
`;
const PillRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`;
const Pill = styled.span`
  background: ${({ $muted }) => ($muted ? "#eef2ff" : "#dcfce7")};
  color: ${({ $muted }) => ($muted ? COLOR.text : "#166534")};
  padding: 8px 10px;
  border-radius: 12px;
  font-weight: 800;
  border: 1px solid ${COLOR.line};
`;
const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;
const YearControl = styled.div`
  display: inline-flex;
  gap: 6px;
  align-items: center;
`;
const NavBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid ${COLOR.line};
  background: #f9f9f9;
  cursor: pointer;
`;
const YearBox = styled.label`
  display: inline-flex;
  gap: 6px;
  align-items: center;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9f9f9;
  color: ${COLOR.text};
  input {
    border: 0;
    background: transparent;
    outline: none;
    width: 90px;
    font-weight: 800;
  }
`;
const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  border: none;
  background: ${COLOR.primary};
  color: #fff;
  font-weight: 800;
  cursor: pointer;
`;
const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.05);
`;
const Bars = styled.div`
  display: grid;
  gap: 10px;
`;
const BarRow = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr 120px;
  gap: 8px;
  align-items: center;
  .label {
    font-weight: 800;
    color: ${COLOR.text};
  }
  .val {
    text-align: right;
    font-weight: 800;
    color: ${COLOR.text};
  }
`;
const BarTrack = styled.div`
  height: 12px;
  border-radius: 999px;
  background: #eef2ff;
  border: 1px solid #e0e7ff;
  overflow: hidden;
`;
const BarFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, var(--app-primary-hover), ${COLOR.primary});
`;
const Center = styled.div`
  text-align: center;
  color: ${COLOR.muted};
  padding: 16px;
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top-color: ${COLOR.primary};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
